'use strict';

/* globals jQuery Stripe yithStripePayments yithStripePaymentsElement */

import { $, $body } from '../globals';
import PaymentElement from './PaymentElement';
import AjaxHandler from './ajax';

/**
 * Class that init and manages all interactions with Stripe Element on Checkout
 */
export default class PaymentElementCheckout extends PaymentElement {
	isCheckout = true;

	/* === INITIALIZATION METHOD === */

	/**
	 * Init current object
	 */
	init() {
		super.init();

		$body.on( 'updated_checkout', this.updateElements );
		$body.on( 'checkout_error', this.clearPaymentMethod );
	}

	/**
	 * Init checkout handling
	 */
	initFormSubmit() {
		this.getForm()
			.on(
				`checkout_place_order_${ yithStripePaymentsElement.slug }`,
				this.onSubmit
			)
			.on( 'change', ':input', this.clearPaymentMethod );
	}

	/**
	 * Checks if current form already has a payment method or needs a new one
	 *
	 * @return {boolean} Whether form contains payment method or not
	 */
	hasPaymentMethod() {
		return super.hasPaymentMethod() || !! this.getSelectedToken();
	}

	/* === GETTERS METHOD === */

	/**
	 * Get an object with billing details about customer
	 *
	 * @return {Object} Billing object.
	 */
	getBillingDetails() {
		const $form = this.getForm(),
			$billingFields = $form.find( '[name^=billing]' );

		if ( ! $billingFields.length ) {
			return {};
		}

		const address = {};

		$billingFields.each( ( i, v ) => {
			const $field = $( v ),
				name = $field.attr( 'name' ).replace( 'billing_', '' );

			address[ name ] = $field.val();
		} );

		const name = `${ address.first_name } ${ address.last_name }`;

		return {
			name,
			email: address?.email,
			phone: address?.phone,
			address: {
				country: address?.country,
				city: address?.city,
				line1: address?.address_1,
				line2: address?.address_2,
				postal_code: address?.postcode,
				state: address.state,
			},
		};
	}

	/**
	 * Returns selected payment method from existing ones, if any
	 *
	 * @return {string|boolean} Selected token among existing ones, or false if no selection is made (or if no token exists)
	 */
	getSelectedToken() {
		const $form = this.getForm(),
			$tokens = $form.find(
				`[name="wc-${ yithStripePaymentsElement.slug }-payment-token"]`
			),
			$selectedToken = $tokens.filter( ':selected' );

		if ( ! $selectedToken.length ) {
			return false;
		}

		const selectedToken = $selectedToken.val();

		if ( ! selectedToken ) {
			return false;
		}

		return selectedToken;
	}

	/**
	 * Returns an object with a set of options to be used for Elements init/update
	 *
	 * @return {Promise<object|boolean>} Settings object, or false on failure.
	 */
	async getElementsDetails() {
		let amount, currency, secret, mode;

		try {
			const checkoutDetails = await this.getCheckoutDetails();

			amount = checkoutDetails?.total;
			currency = checkoutDetails?.currency;
			secret = checkoutDetails?.secret;
			mode = amount ? 'payment' : 'setup';
		} catch ( e ) {
			return false;
		}

		let details = {
			paymentMethodCreation: 'manual',
			appearance: yithStripePaymentsElement.appearance,
		};

		if ( secret ) {
			details.clientSecret = secret;
		} else {
			details = {
				...details,
				mode,
				currency,
				captureMethod: yithStripePaymentsElement.capture_method,
			};

			if ( 'payment' === mode ) {
				details.amount = amount;
			}
		}

		if ( !! yithStripePaymentsElement.tokenization ) {
			details.setupFutureUsage = 'on_session';
		}

		return details;
	}

	/**
	 * Retrieves fresh checkout details via AJAX
	 *
	 * @return {Promise<object>} JSON object retrieve form AJAX call, containing details about the checkout, such as amount and currency
	 */
	async getCheckoutDetails() {
		return await AjaxHandler.get.call(
			null,
			'get_checkout_details',
			'get_checkout_details',
			{
				gateway: 'element',
			}
		);
	}
}
